import service from 'utils/service';

export type CustomerProfileRecord = {
  ID?: number;
  NAME?: string;
  MOBILE_NO?: string;
  EMAIL_ID?: string;
  FIRM_NAME?: string;
  ADDRESS?: string | null;
  PINCODE_ID?: number | string | null;
  CITY?: string | null;
  STATE_ID?: number | string | null;
  STATUS?: number;
};

export const getLoginCustomerId = (): number | null => {
  try {
    const loginCustomer = JSON.parse(localStorage.getItem('loginCustomer') || '{}');
    const id = loginCustomer?.[0]?.UserData?.[0]?.CUSTOMER_ID;
    return id != null ? Number(id) : null;
  } catch {
    return null;
  }
};

export const buildCustomerGetBody = (customerId: number) => ({
  ID: [Number(customerId)]
});

export const normalizeCustomerGetResponse = (response: any): CustomerProfileRecord | null => {
  const data = response?.data;
  if (Array.isArray(data) && data.length) return data[0];
  if (data && typeof data === 'object') return data;
  return null;
};

const hasValue = (value: unknown) => {
  if (value == null) return false;
  return String(value).trim().length > 0;
};

/** True when customer has a non-empty business ADDRESS and STATE_ID (matches legacy). */
export const isAddressProfileComplete = (customer: CustomerProfileRecord | null): boolean => {
  if (!customer) return false;
  return hasValue(customer.ADDRESS) && hasValue(customer.STATE_ID);
};

export const ADDRESS_PROFILE_REQUIRED_KEY = 'addressProfileRequired';

export const ADDRESS_PROFILE_SKIP_MESSAGE = 'Please update business address to continue';

const ADDRESS_PROFILE_SKIP_TOAST_KEY = 'addressProfileSkipToast';

export const markAddressProfileSkipToast = () => {
  sessionStorage.setItem(ADDRESS_PROFILE_SKIP_TOAST_KEY, '1');
};

export const consumeAddressProfileSkipToast = () => {
  const shouldShow = sessionStorage.getItem(ADDRESS_PROFILE_SKIP_TOAST_KEY) === '1';
  if (shouldShow) sessionStorage.removeItem(ADDRESS_PROFILE_SKIP_TOAST_KEY);
  return shouldShow;
};

export const setAddressProfileRequired = () => {
  sessionStorage.setItem(ADDRESS_PROFILE_REQUIRED_KEY, '1');
};

export const clearAddressProfileRequired = () => {
  sessionStorage.removeItem(ADDRESS_PROFILE_REQUIRED_KEY);
};

/** Set after address is verified so dashboard/header APIs are not fired before redirect. */
const ADDRESS_PROFILE_SHELL_KEY = 'addressProfileShellReady';

export const markAddressProfileShellReady = () => {
  sessionStorage.setItem(ADDRESS_PROFILE_SHELL_KEY, '1');
};

export const isAddressProfileShellReady = () => sessionStorage.getItem(ADDRESS_PROFILE_SHELL_KEY) === '1';

export const clearAddressProfileShellReady = () => {
  sessionStorage.removeItem(ADDRESS_PROFILE_SHELL_KEY);
};

export const clearAddressProfileSession = () => {
  clearAddressProfileRequired();
  clearAddressProfileShellReady();
  sessionStorage.removeItem(ADDRESS_PROFILE_SKIP_TOAST_KEY);
};

export const isAddressProfileRequired = () => sessionStorage.getItem(ADDRESS_PROFILE_REQUIRED_KEY) === '1';

export const fetchCustomerProfile = async (customerId: number): Promise<CustomerProfileRecord | null> => {
  const response: any = await service.makeAPICall({
    methodName: service.Methods.POST,
    apiUrl: service.API_URL.user.listing,
    body: buildCustomerGetBody(customerId)
  });
  if (response?.code !== 200) return null;
  return normalizeCustomerGetResponse(response);
};
